import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { AllMeetingStatus, MeetingRecord } from 'type/meeting';
import MeetingInfo from './MeetingInfo';
import noMeetingImg from 'asset/image/kts_meeting.png';
import style from './Schedule.module.scss';
import moment from 'moment';

interface IProps {
  from?: string;
  hasMore?: boolean;
  loading?: boolean;
  mode?: 'default' | 'day' | 'upcoming';
  onLoadMore?: () => void;
  records: MeetingRecord[];
  refresh?: any;
  showLoadMore?: boolean;
}

type UpcomingGroup = {
  date: string;
  title: string;
  records: MeetingRecord[];
};

const WEEK_TEXT = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];

const getDateTitle = (time: number) => {
  const date = moment(time).startOf('d');
  const dateText = `${date.format('MM-DD')} ${WEEK_TEXT[date.day()]}`;

  if (date.isSame(moment().startOf('d'), 'day')) {
    return `今天 · ${dateText}`;
  }

  if (date.isSame(moment().add(1, 'd').startOf('d'), 'day')) {
    return `明天 · ${dateText}`;
  }

  if (!date.isSame(moment(), 'year')) {
    return `${date.format('YYYY-MM-DD')} ${WEEK_TEXT[date.day()]}`;
  }

  return dateText;
};

const groupByDate = (records: MeetingRecord[]) => {
  const groups: UpcomingGroup[] = [];
  const sorted = [...records].sort((r1, r2) => r1.meeting.beginTime - r2.meeting.beginTime);

  sorted.forEach((record) => {
    const date = moment(record.meeting.beginTime).format('YYYY-MM-DD');
    const group = groups.find((item) => item.date === date);

    if (group) {
      group.records.push(record);
      return;
    }

    groups.push({
      date,
      title: getDateTitle(record.meeting.beginTime),
      records: [record]
    });
  });

  return groups;
};

const Schedule: React.FC<IProps> = (props) => {
  const {
    from,
    hasMore = false,
    loading = false,
    mode = 'default',
    onLoadMore,
    records,
    refresh,
    showLoadMore = false
  } = props;
  const isUpcomingMode = mode === 'upcoming';
  const isDayMode = mode === 'day';
  const showLoadMoreStatus = showLoadMore && records && records.length > 0;

  const [group, setGroup] = useState({
    yet: [],
    past: []
  } as { yet: MeetingRecord[]; past: MeetingRecord[] });

  useEffect(() => {
    if (!records || !records.length) {
      setGroup({
        yet: [],
        past: []
      });
      return;
    }

    const yet: MeetingRecord[] = [];
    const past: MeetingRecord[] = [];

    const sorted = [...records].sort((r1, r2) => r1.meeting.beginTime - r2.meeting.beginTime);

    const curTime = moment().valueOf();
    sorted.forEach((r) => {
      if (r.meeting.status === AllMeetingStatus.FINISH || r.meeting.status === AllMeetingStatus.FILED || curTime >= r.meeting.endTime) {
        past.push(r);
      } else {
        yet.push(r);
      }
    });
    setGroup({
      yet,
      past
    });
  }, [records]);

  return (
    <div className={style.warp}>
      <Spin spinning={loading}>
        {records && records.length > 0 ? (
          <>
            {isUpcomingMode ? (
              <>
                <div className={style.groupTitle}>待开始会议</div>
                <div>
                  {groupByDate(records).map((item) => (
                    <div className={style.dateGroup} key={item.date}>
                      <div className={style.dateTitle}>{item.title}</div>
                      <div>
                        {item.records.map((r) => (
                          <MeetingInfo key={`${item.date}-${r.meeting.meetingNo}`} refresh={refresh} from={from} record={r} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                {group.yet.length > 0 && (
                  <>
                    {!isDayMode && <div className={style.groupTitle}>当日安排</div>}
                    <div>
                      {group.yet.map((r) => (
                        <MeetingInfo key={r.meeting.meetingNo} refresh={refresh} from={from} record={r} />
                      ))}
                    </div>
                  </>
                )}
                {group.past.length > 0 && (
                  <>
                    <div className={style.groupTitle} style={{ marginTop: group.yet.length > 0 ? '38px' : 0 }}>
                      已结束
                    </div>
                    <div>
                      {group.past.map((r) => (
                        <MeetingInfo key={r.meeting.meetingNo} from={from} record={r} />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
            {showLoadMoreStatus && hasMore && (
              <div className={style.loadMore}>
                <span
                  className={loading ? style.loadMoreDisabled : style.loadMoreLink}
                  onClick={loading ? undefined : onLoadMore}
                >
                  {loading ? '加载中...' : '点击加载更多'}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className={style.noMeetingTips}>
            <img src={noMeetingImg} className={style.img}></img>
            <div className={style.text}>暂无会议安排</div>
          </div>
        )}
      </Spin>
    </div>
  );
};

export default Schedule;
